import React from "react";
import { Box, Container, Typography, Chip } from "@mui/material";
import { motion } from "framer-motion";
import BRAND from "../theme/talktokam";

const entries = [
  {
    version: "v2.0.0",
    date: "March 2025",
    tag: "Major",
    color: "#6366f1",
    changes: [
      "AI Agents � 6 pre-built personas (Sales, Support, FAQ, Lead Qualifier, Appointment, Custom)",
      "Live Chat � Real-time messaging with agent queue, transfer, and canned responses",
      "Flow Builder Redesign � New drag-and-drop canvas with 19 question types",
      "Analytics Dashboard � Live KPIs with 30-second auto-refresh and 7-day trends",
      "WhatsApp Integration � Catalog, e-commerce, and chat management",
    ],
  },
  {
    version: "v1.5.0",
    date: "December 2024",
    tag: "Major",
    color: "#6366f1",
    changes: [
      "Multi-Language Support � UI translations for 5 languages",
      "Domain Whitelist � Per-bot security control for widget embedding",
      "Webhook Support � Real-time event-driven data export",
      "Lead Data Masking � Privacy-first lead handling",
      "CSV Export � Download conversations for external analysis",
    ],
  },
  {
    version: "v1.3.0",
    date: "September 2024",
    tag: "Minor",
    color: "#10b981",
    changes: [
      "Google Sheets Integration � Auto-sync leads and conversations",
      "Email OTP Validation � Verify user emails in flows",
      "Auto Email Followup � Automated lead nurturing sequences",
      "Performance improvements and bug fixes",
    ],
  },
  {
    version: "v1.2.0",
    date: "June 2024",
    tag: "Minor",
    color: "#10b981",
    changes: [
      "Appointment Booking � Full appointment scheduling question type",
      "Order Items � E-commerce order management flow",
      "Dynamic Questions � API-powered dynamic options",
      "Real-time Search � Live search through external APIs",
    ],
  },
  {
    version: "v1.1.0",
    date: "March 2024",
    tag: "Patch",
    color: "#f59e0b",
    changes: [
      "Form Question Type � Multi-field form capture",
      "File Upload � Allow visitors to upload files",
      "Rating & Feedback � Collect ratings in-flow",
      "Enhanced analytics with per-bot metrics",
    ],
  },
  {
    version: "v1.0.0",
    date: "January 2024",
    tag: "Initial",
    color: "#f59e0b",
    changes: [
      "Bot Builder � Create and manage multiple chatbots",
      "Visual Flow Editor � Basic node-based conversation designer",
      "Widget Embed � JavaScript snippet for any website",
      "Basic Analytics � Conversation counts and ratings",
      "Authentication � Email/password login and teams",
    ],
  },
];

const Changelog = () => {
  return (
    <Box sx={{ background: "#050816", minHeight: "100vh" }}>
      <Box
        sx={{
          position: "relative",
          textAlign: "center",
          py: { xs: 10, md: 14 },
          background: `radial-gradient(ellipse 800px 600px at 20% 10%, ${BRAND.cyan}33 0%, transparent 60%), radial-gradient(ellipse 700px 500px at 85% 90%, ${BRAND.purple}33 0%, transparent 60%), radial-gradient(ellipse 600px 400px at 50% 50%, ${BRAND.primary}1a 0%, transparent 60%), linear-gradient(180deg, #050816 0%, #0a0e27 50%, #050816 100%)`,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage: `linear-gradient(rgba(56,189,248,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(56,189,248,0.04) 1px, transparent 1px)`,
            backgroundSize: "60px 60px",
            maskImage:
              "radial-gradient(ellipse at center, black 30%, transparent 70%)",
            WebkitMaskImage:
              "radial-gradient(ellipse at center, black 30%, transparent 70%)",
            pointerEvents: "none",
          }}
        />
        <motion.div
          animate={{ y: [0, -40, 0], scale: [1, 1.1, 1] }}
          transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
          style={{
            position: "absolute",
            top: "5%",
            left: "-5%",
            width: 500,
            height: 500,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${BRAND.cyan}40 0%, transparent 70%)`,
            filter: "blur(60px)",
            pointerEvents: "none",
          }}
        />
        <motion.div
          animate={{ y: [0, 40, 0], scale: [1, 1.15, 1] }}
          transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }}
          style={{
            position: "absolute",
            bottom: "-10%",
            right: "-10%",
            width: 600,
            height: 600,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${BRAND.purple}40 0%, transparent 70%)`,
            filter: "blur(80px)",
            pointerEvents: "none",
          }}
        />
        <motion.div
          animate={{ y: [0, -25, 0], x: [0, 20, 0] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
          style={{
            position: "absolute",
            top: "50%",
            right: "30%",
            width: 300,
            height: 300,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${BRAND.primary}26 0%, transparent 70%)`,
            filter: "blur(40px)",
            pointerEvents: "none",
          }}
        />
        <Container maxWidth="md" sx={{ position: "relative", zIndex: 1 }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                fontSize: { xs: "2rem", md: "3rem" },
                background: "linear-gradient(135deg, #38bdf8, #a855f7)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                mb: 2,
              }}
            >
              What's New at Talktokam
            </Typography>
            <Typography
              sx={{
                fontSize: "1.1rem",
                color: "#94a3b8",
                maxWidth: 500,
                mx: "auto",
              }}
            >
              We're constantly shipping improvements. Here's what changed.
            </Typography>
          </motion.div>
        </Container>
      </Box>

      <Container sx={{ py: { xs: 6, md: 10 }, maxWidth: 800 }}>
        {entries.map((entry, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.1 }}
          >
            <Box
              sx={{
                position: "relative",
                pl: 4,
                pb: 4,
                borderLeft:
                  i < entries.length - 1
                    ? "2px solid rgba(255,255,255,0.08)"
                    : "none",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  left: -8,
                  top: 4,
                  width: 14,
                  height: 14,
                  borderRadius: "50%",
                  bgcolor: entry.color,
                  boxShadow: `0 0 10px ${entry.color}`,
                }}
              />
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}
              >
                <Typography
                  sx={{ fontWeight: 800, fontSize: "1.1rem", color: "#fff" }}
                >
                  {entry.version}
                </Typography>
                <Chip
                  label={entry.tag}
                  size="small"
                  sx={{
                    fontSize: "0.65rem",
                    fontWeight: 700,
                    bgcolor: `${entry.color}25`,
                    color: entry.color,
                  }}
                />
                <Typography sx={{ fontSize: "0.8rem", color: "#94a3b8" }}>
                  {entry.date}
                </Typography>
              </Box>
              <Box component="ul" sx={{ m: 0, pl: 1.5, listStyle: "none" }}>
                {entry.changes.map((c, j) => (
                  <Typography
                    key={j}
                    component="li"
                    sx={{
                      fontSize: "0.9rem",
                      color: "#cbd5e1",
                      mb: 0.5,
                      lineHeight: 1.6,
                      "&::before": { content: '"\\2022 "', color: "#38bdf8" },
                    }}
                  >
                    {c}
                  </Typography>
                ))}
              </Box>
            </Box>
          </motion.div>
        ))}
      </Container>
    </Box>
  );
};

export default Changelog;
